/*
 * LES RACCOURCIS DU DOCK VIENNENT DES PORTES, pas d'une liste ecrite a la main.
 *
 * LE PROBLEME QU'IL FERME. Le panneau Dock des Parametres ne proposait que les
 * actions recopiees dans la fiche d'une app (`taki.app.json`, champ `actions`) :
 * une app neuve, ou une porte ajoutee par un client dans son app lite, n'y
 * apparaissait jamais, parce que personne n'avait pense a recopier. La porte,
 * elle, declare deja son geste de creation (`kind: "add"` dans
 * `taki.screens.json`) : c'est la seule source, et le dock la lit.
 *
 * SEULES LES CREATIONS DEVIENNENT DES RACCOURCIS. Les autres actions d'une porte
 * (« Mettre en vitrine », « Copier les references ») portent sur une fiche ou
 * une selection : lancees depuis le dock, elles n'auraient rien sur quoi agir.
 *
 * LECTURE STRUCTURELLE, comme `portesDeclarees` (`navigation.ts`) : ce package
 * n'a pas `@taki/data-model`, et une declaration qui ne se relit pas donne zero
 * raccourci, jamais une exception dans le dock.
 */
import type { AccesPort } from '@taki/ui';
import type { AppActionDecl } from './types.js';

const estObjet = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const chaine = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v : undefined;

/**
 * Icone d'un raccourci de porte quand l'app n'en donne pas. D'ordinaire c'est
 * l'ICONE DE L'APP qui est proposee (`icone`) : le dock pose deja un « + » lime
 * sur chaque raccourci, et des raccourcis tous dessines d'un « + » ne se
 * distingueraient plus que par leur infobulle.
 */
export const ICONE_DE_CREATION = 'IconPlus';

/**
 * L'ID D'UN RACCOURCI DE PORTE, celui que la pref `dock.shortcuts` retient. La
 * porte ET l'action : chaque porte appelle son ajout `ajouter`, l'id seul ne
 * dirait donc pas laquelle.
 */
export function idDeRaccourciDePorte(porteId: string, actionId: string): string {
  return `${porteId}/${actionId}`;
}

/**
 * LES CREATIONS DES PORTES DU PREMIER ECRAN d'un `taki.screens.json` (une app
 * en monte un, comme `portesDeclarees`), dans l'ordre du menu. Le libelle nomme
 * la PORTE puis le geste (« Personnes : Ajouter ») : le geste seul serait le
 * meme mot sur toutes les lignes.
 */
export function actionsDesPortes(
  ecrans: unknown,
  /** L'icone proposee d'office : celle de l'app (le nom de sa fiche). */
  icone: string = ICONE_DE_CREATION,
): AppActionDecl[] {
  if (!estObjet(ecrans) || !Array.isArray(ecrans.ecrans)) return [];
  const premier: unknown = ecrans.ecrans[0];
  if (!estObjet(premier) || !Array.isArray(premier.portes)) return [];
  const actions: AppActionDecl[] = [];
  for (const porte of premier.portes as unknown[]) {
    if (!estObjet(porte) || !Array.isArray(porte.actions)) continue;
    const porteId = chaine(porte.id);
    if (porteId === undefined) continue;
    /* UNE PORTE HORS NAVIGATION n'a pas de raccourci : on n'y cree que depuis
       la fiche qui la lie (une tache naitrait sinon sans son projet). */
    if (porte.navigation === false) continue;
    const nomDePorte = chaine(porte.label) ?? porteId;
    const family = estObjet(porte.jeu) ? chaine(porte.jeu.family) : undefined;
    const type = estObjet(porte.jeu) ? chaine(porte.jeu.type) : undefined;
    const jeu = family === undefined ? undefined : { family, ...(type === undefined ? {} : { type }) };
    for (const action of porte.actions as unknown[]) {
      if (!estObjet(action) || action.kind !== 'add') continue;
      const actionId = chaine(action.id);
      if (actionId === undefined) continue;
      actions.push({
        id: idDeRaccourciDePorte(porteId, actionId),
        label: `${nomDePorte} : ${chaine(action.label) ?? 'Ajouter'}`,
        iconName: icone,
        porte: { id: porteId, actionId, ...(jeu === undefined ? {} : { jeu }) },
      });
    }
  }
  return actions;
}

/**
 * LES ACTIONS D'UNE APP POUR LE DOCK : celles ecrites a la main (une app sans
 * portes declarees, le cockpit), puis celles de ses portes. Une action ecrite a
 * la main qui porte deja une porte est une ancienne derivation : elle est
 * remplacee, jamais doublee.
 */
export function actionsDuDock(
  ecrites: readonly AppActionDecl[] | undefined,
  ecrans: unknown,
  icone?: string,
): AppActionDecl[] {
  return [
    ...(ecrites ?? []).filter((action) => action.porte === undefined),
    ...actionsDesPortes(ecrans, icone),
  ];
}

/**
 * CE MEMBRE PEUT-IL SE SERVIR DE CE RACCOURCI ? Une creation dans une porte ou
 * il n'ecrit pas ouvrirait la porte sans rien y creer (le moteur refuse la
 * fiche vide en lecture seule) : le dock ne la montre pas, le panneau ne la
 * propose pas.
 *
 * DANS LE DOUTE, OUI : sans port d'acces, avant que la fiche membre soit lue,
 * ou pour une action ecrite a la main (elle ne nomme pas de jeu). Masquer sur
 * une absence d'information ferait clignoter le dock a chaque chargement ; les
 * rules, elles, refusent toujours ce qui doit l'etre.
 */
export function raccourciUtilisable(action: AppActionDecl, acces: AccesPort | null): boolean {
  const jeu = action.porte?.jeu;
  if (jeu === undefined || acces === null || !acces.pret) return true;
  return acces.niveau(jeu.family, jeu.type) === 'write';
}
